import { useMemo, useState } from "react";
import {
  useAdminPanelToggleContextReady,
  useInstagramApiPublishingOn,
} from "../../../../utils/feature-toggle-utils";
import Bugsnag from "@bugsnag/js";
import { useDispatch } from "react-redux";
import { ChunkLoadRecoveryModal } from "../../../chunk-load-recovery-modal";
import { Button } from "../../../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../ui/tooltip";
import { FIELDS } from "../../../../constants/PostFormFields";
import { openModal } from "../../../../libs/modals";
import { POST_LOCATION_MODAL, POST_TAGS_MODAL } from "../modals/modal-types";
import {
  ensurePostTagsModalRegistered,
  ensurePostLocationModalRegistered,
} from "../modals/lazy-registration";
import { toast } from "react-toastify";
import { POST_TYPES } from "../../../../constants/PostsView";
import { ADD_COLLABORATOR_MODAL } from "../components/CollaboratorsModal/index";
import type { FormPlatformType } from "../../../../shared/types/post";
import type { AccountType } from "../../../../shared/types/accounts";
import { firstTagAsset, isVideoAsset, resolveTagStillUrl } from "./tagStill";
import {
  buildTagSlides,
  countTags,
  hasPerStoryUserTags,
  isPerSlideTagPost,
  readSlideTags,
  reconcileTagStorage,
  writeSlideTags,
  type TagArrayKey,
  type TagStoragePost,
} from "./carousel-tag-slides";
import {
  apiCapableInstagramAccounts,
  everyInstagramAccountApiCapable,
  hasApiCapableInstagramAccount,
  TagCapabilityWarning,
  type TagCapabilityAccount,
} from "./TagCapabilityWarning";
import { isChunkLoadError } from "../../../../utils/is-chunk-load-error";
import { reloadPage } from "../../../../utils/reload-page";
import { toError } from "../../../../utils/to-error";
import PlatformUsage from "../PlatformUsage/PlatformUsage";

const FIGMA_ADD_BUTTON_WIDTHS: Record<string, number> = {
  "Add location": 112,
  "Add collaborators": 142,
  "Add user tags": 120,
  "Add product tags": 139,
  "Add alt text": 106,
};

type PostTagMediaFile = {
  isVideo?: boolean;
  altText?: string | string[];
};

/*
 * Carries the capability fields as well as the display ones, because
 * `TagCapabilityWarning` decides from them whether an account will silently
 * drop the customer's tags. Declared here rather than left to a cast, so
 * trimming what this component is handed is a compile error rather than a
 * warning that quietly starts naming every account.
 */
type PostTagAccount = Pick<AccountType, "_id"> &
  TagCapabilityAccount & {
    platformName?: string;
    platformType?: string;
  };

type Collaborator = {
  username: string;
  profilePictureUrl?: string;
};

type PostTagLocation = { name?: string } | null | undefined;

type PostTagValue = {
  location?: PostTagLocation;
  collaboratingIgUsers?: Collaborator[];
  tags?: unknown[];
  productTags?: unknown[];
  mediaFiles?: PostTagMediaFile[];
  type?: string;
  altText?: string;
  accountIds?: string[];
  accounts: PostTagAccount[];
  isStory?: boolean;
};

const DisabledOptionHelp = ({
  label,
  message,
}: {
  label: string;
  message: string;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="flex h-6 w-[15px] shrink-0 cursor-pointer items-center justify-center rounded-full p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          aria-label={`Why ${label} is disabled`}
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <span
            className="flex size-[15px] shrink-0 items-center justify-center rounded-full bg-neutral-700 font-inter text-xs font-normal leading-[15px] text-white"
            aria-hidden="true"
          >
            ?
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-64 text-left">
        {message}
      </TooltipContent>
    </Tooltip>
  );
};

const ensureModalAvailable = async (
  register: () => Promise<void>,
  errorMessage: string,
  context: string,
  onChunkLoadError: () => void
): Promise<boolean> => {
  try {
    await register();
    return true;
  } catch (error) {
    const normalizedError = toError(error);
    Bugsnag.notify(normalizedError, (event) => {
      event.context = context;
    });

    if (isChunkLoadError(normalizedError)) {
      onChunkLoadError();
      return false;
    }

    toast.error(errorMessage);
    return false;
  }
};
interface PostTagsProps {
  post: PostTagValue;
  selectedPlatform: FormPlatformType;
  platforms: unknown[];
  visibleFields: string[];
  className?: string;
  onChange: (field: string, value: unknown) => void;
  handleOpenAddAlt: () => void;
  isSummary?: boolean;
  hasTikTokPosting?: boolean;
}

const PostTags = ({
  post,
  selectedPlatform,
  platforms,
  visibleFields,
  className = "",
  onChange,
  handleOpenAddAlt,
  isSummary,
}: PostTagsProps) => {
  const dispatch = useDispatch();
  const [requiresReload, setRequiresReload] = useState(false);

  const location = post.location;

  const collaboratorIg = post.collaboratingIgUsers;

  /*
   * SKE-4617. Counted wherever they are stored. A carousel holds its tags on
   * the slides, so reading the post-level arrays alone would report "no user
   * tags" on a post the customer has tagged.
   */
  /*
   * SKE-4733. The Instagram API publishing gate. Off, the composer keeps the
   * phone path's shape: no API-only tagging, legacy limits, post-level tags.
   */
  const apiPublishingOn = useInstagramApiPublishingOn();
  // SKE-4733 review. Until the toggles arrive the gate reads off, so the tagger
  // must not open and reshape tag storage on that guess.
  const toggleContextReady = useAdminPanelToggleContextReady();
  /*
   * SKE-4714. A story set is tagged story by story only when every selected
   * Instagram account is on the API track. Otherwise it keeps the single
   * post-level tagger master shipped, with the capability warning below.
   *
   * A set that ALREADY carries per-story tags stays per story while the gate is
   * on, even if a phone-only account has joined the selection since. The API
   * accounts still publish those tags story by story, and collapsing to the
   * post level would show no tags and wipe them on the next open.
   */
  const perStoryTagging = useMemo(
    () =>
      everyInstagramAccountApiCapable(post.accounts, apiPublishingOn) ||
      (apiPublishingOn &&
        Boolean(post.isStory) &&
        hasPerStoryUserTags(post as TagStoragePost)),
    [post, apiPublishingOn]
  );
  const userTagCount = countTags(
    post as TagStoragePost,
    "tags",
    apiPublishingOn,
    perStoryTagging
  );
  const productTagCount = countTags(
    post as TagStoragePost,
    "productTags",
    apiPublishingOn,
    perStoryTagging
  );

  const canAltText = true;
  const hasImageMedia = useMemo(() => {
    if (!post?.mediaFiles || post.mediaFiles.length === 0) {
      return (
        post.type === POST_TYPES.PHOTO ||
        post.type === POST_TYPES.IMAGE ||
        post.type === POST_TYPES.MULTI_MEDIA
      );
    }

    return post.mediaFiles.some(
      (mediaFile: PostTagMediaFile) => !mediaFile.isVideo
    );
  }, [post]);

  const hasAlt = canAltText && hasImageMedia;

  const altText = useMemo(() => {
    if (!post) {
      return [];
    }
    if (!post.altText) {
      if (post.mediaFiles && post.mediaFiles.length < 1) {
        return [];
      } else {
        // continue - use post.alttext if no mediaFiles items
      }
    }
    if (post.mediaFiles && post.mediaFiles.length > 0) {
      return post.mediaFiles.flatMap((file: PostTagMediaFile) =>
        file.altText ? file.altText : []
      );
    } else {
      return post.altText ? [post.altText] : [];
    }
  }, [post]);

  function getIGAccountId() {
    let accountId;
    if ((post.accountIds?.length ?? 0) > 1) {
      const result = post.accounts.find(
        (perAccount: PostTagAccount) =>
          perAccount.platformName === "instagram" ||
          perAccount.platformType === "IG"
      );
      accountId = result?._id;
    } else {
      accountId = post.accountIds?.[0];
    }
    return accountId;
  }

  /*
   * SKE-4539. A user tag needs a still to be positioned against. A video has
   * one as soon as its cover frame or generated first frame exists, so the only
   * real blocker is an upload still being processed. An image is its own still,
   * and the modal derives that itself, so the requirement is video-only.
   *
   * SKE-4612. ONE answer to "is the first asset a video", used by every gate
   * here. There were two: this one, and a `firstMediaIsVideo` that read only
   * `mediaFiles[0].isVideo` and returned false when `mediaFiles` was empty.
   * A post in the older FLAT shape with an `.mp4` url therefore opened the
   * product-tag and location editors as though it were an image, while the
   * capability warning beside them treated the same post as a video. The flat
   * shape is supported deliberately in `firstTagAsset`, so that is reachable,
   * not theoretical.
   */
  const tagStillUrl = useMemo(() => resolveTagStillUrl(post as never), [post]);
  const firstAssetIsVideo = useMemo(
    () => isVideoAsset(firstTagAsset(post as never)),
    [post]
  );

  const stillIsProcessing = firstAssetIsVideo && !tagStillUrl;

  /*
   * SKE-4609. Meta REJECTS a tag position on non-story video and fails the
   * whole container, so the publish path strips coordinates there. Instagram
   * lists tags on a reel rather than placing them, so the position never had
   * meaning on that surface: the username still publishes, and only the
   * placement is dropped.
   *
   * The editor stays, because it is how a username gets entered and because
   * `sanitizeTags` discards any tag whose coordinates are not numbers, so a
   * position-free tag would vanish when a saved draft reopened. What changes is
   * that the modal now SAYS the position will not be used, instead of letting
   * the customer place a pin that is quietly discarded.
   */
  const tagPositionsIgnored = firstAssetIsVideo && !post?.isStory;

  /*
   * SKE-4499. Product tags and a location were refused on any video, because
   * the phone path cannot carry either. The API path can: Meta takes
   * `product_tags` on a reel (id only, no coordinates, which the publish path
   * already strips) and `location_id` on one too.
   *
   * So the refusal now depends on how the post can publish rather than on what
   * the media is. With no API-capable account selected, this is exactly today's
   * behaviour.
   */
  const apiPublishingAvailable = useMemo(
    () => hasApiCapableInstagramAccount(post.accounts, apiPublishingOn),
    [post.accounts, apiPublishingOn]
  );

  const videoBlocksTagging = firstAssetIsVideo && !apiPublishingAvailable;

  /*
   * SKE-4538, SKE-4543. Only the capable accounts are sent to the location and
   * catalog lookups: each refuses the WHOLE request when one account it is given
   * has no usable Instagram token, and a refusal there costs the customer their
   * search.
   */
  const apiCapableAccountIds = useMemo(
    () =>
      apiCapableInstagramAccounts(post.accounts, apiPublishingOn)
        .map((account) => account._id)
        .filter((accountId): accountId is string => Boolean(accountId)),
    [post.accounts, apiPublishingOn]
  );

  const alertFirstMediaVideo = () => {
    toast.error("You can't tag the post if the first media asset is a video.");
  };

  /*
   * SKE-4617. The tagger opens on the storage the publish path will actually
   * read, moving the tags there first when the two disagree.
   *
   * A carousel carrying post-level tags gets them collected onto slide one,
   * which is where they publish today, and the post-level arrays cleared in the
   * same open. Without that, tagging slide three would take the post into
   * per-slide mode and the slide-one tags would stop being sent, silently:
   * `createContainer` asks `request.media.some(hasItemTags)` once for the whole
   * post and never mixes the two shapes.
   */
  const openTagEditor = async (
    kind: TagArrayKey,
    context: string,
    extraProps: Record<string, unknown> = {}
  ) => {
    if (!toggleContextReady) return;
    const isAvailable = await ensureModalAvailable(
      ensurePostTagsModalRegistered,
      "Failed to load the tag editor. Please try again.",
      context,
      () => setRequiresReload(true)
    );
    if (!isAvailable) return;

    const storagePost = post as TagStoragePost;
    const reconciled = reconcileTagStorage(
      storagePost,
      apiPublishingOn,
      perStoryTagging
    );
    if (reconciled) {
      onChange(FIELDS.MEDIA, reconciled.mediaFiles);
      onChange(FIELDS.USER_TAGS, reconciled.tags);
      onChange(FIELDS.PRODUCT_TAGS, reconciled.productTags);
    }
    const editorPost: TagStoragePost = reconciled
      ? { ...storagePost, ...reconciled }
      : storagePost;

    dispatch(
      openModal({
        type: POST_TAGS_MODAL,
        props: {
          post: editorPost,
          slides: buildTagSlides(editorPost, apiPublishingOn, perStoryTagging),
          slideTags: readSlideTags(
            editorPost,
            kind,
            apiPublishingOn,
            perStoryTagging
          ),
          onSubmit: (slideTags: unknown[][]) => {
            if (
              isPerSlideTagPost(editorPost, apiPublishingOn, perStoryTagging)
            ) {
              onChange(
                FIELDS.MEDIA,
                writeSlideTags(editorPost, kind, slideTags)
              );
              return;
            }
            onChange(
              kind === "tags" ? FIELDS.USER_TAGS : FIELDS.PRODUCT_TAGS,
              slideTags[0] ?? []
            );
          },
          ...extraProps,
        },
      })
    );
  };

  const dispatchUserTagModal = () => {
    /*
     * SKE-4733. A workspace not on the API track cannot tag users on a
     * video-first post, and the phone-era composer said so rather than opening
     * the tagger. An enabled workspace keeps develop's editor, which records the
     * username with the position ignored.
     */
    if (!apiPublishingOn && videoBlocksTagging) {
      alertFirstMediaVideo();
      return;
    }
    return openTagEditor("tags", "PostTags: load user tag modal", {
      type: "USER",
      positionsIgnored: tagPositionsIgnored,
      apiPublishingAvailable,
    });
  };

  const dispatchProductTagModal = () => {
    if (videoBlocksTagging) {
      alertFirstMediaVideo();
      return;
    }
    return openTagEditor("productTags", "PostTags: load product tag modal", {
      type: "PRODUCT",
      positionsIgnored: tagPositionsIgnored,
      apiCapableAccountIds,
      /*
       * SKE-4669. Meta's ceilings are Meta's: a selection that still
       * publishes through the phone app keeps the five product tags and
       * fifteen user tags the composer enforced before this work, because the
       * phone path drops anything past them without a word.
       */
      apiPublishingAvailable,
    });
  };

  const dispatchLocationModal = async () => {
    if (videoBlocksTagging) {
      alertFirstMediaVideo();
      return;
    }
    const isAvailable = await ensureModalAvailable(
      ensurePostLocationModalRegistered,
      "Failed to load the location picker. Please try again.",
      "PostTags: load location modal",
      () => setRequiresReload(true)
    );
    if (!isAvailable) return;
    dispatch(
      openModal({
        type: POST_LOCATION_MODAL,
        props: {
          location: post.location,
          apiCapableAccountIds,
          accounts: post.accounts,
          onSubmit: (newLocation: PostTagLocation) => {
            onChange(FIELDS.LOCATION_TAG, newLocation);
          },
        },
      })
    );
  };
  const dispatchCollaboratorModal = () => {
    dispatch(
      openModal({
        type: ADD_COLLABORATOR_MODAL,
        props: {
          collaboratorIg: collaboratorIg,
          beta: true,
          title: "Add Collaborators",
          notification:
            "If they accept, their usernames will be added to the Instagram post. It will be shared to their followers and shown on their profile. Maximum 3 collaborators.",
          limit: 3,
          onSubmit: (Collaborator: Collaborator[]) => {
            const collaboratorsNames = Collaborator.map(
              (aCollaborator: Collaborator) => {
                return {
                  username: aCollaborator.username,
                  profilePictureUrl: aCollaborator.profilePictureUrl,
                };
              }
            );
            onChange(FIELDS.COLLABORATOR_TAG, collaboratorsNames);
            if (Collaborator.length > 0) {
              toast.success(
                `Pow! ${Collaborator.length} collaborator(s) added. `
              );
            }
          },
          accountId: getIGAccountId(),
        },
      })
    );
  };
  // only show the field label and field if any of the fields are visible
  // so that it doesn't show a heading with an empty section
  if (
    visibleFields.includes(FIELDS.LOCATION_TAG) ||
    visibleFields.includes(FIELDS.COLLABORATOR_TAG) ||
    visibleFields.includes(FIELDS.USER_TAGS) ||
    visibleFields.includes(FIELDS.PRODUCT_TAGS) ||
    (hasAlt && visibleFields.includes(FIELDS.ALT_TEXT))
  ) {
    const hasProductTags = productTagCount > 0;
    const hasUserTags = userTagCount > 0;
    const altTextCount = altText.filter(
      (value) => typeof value === "string" && value.trim().length > 0
    ).length;

    const hasUserAndProductTags = hasProductTags && hasUserTags; // for access for legacy post to change tags value

    /*
     * SKE-4499. Meta imposes no mutual exclusion between user tags and product
     * tags; the phone path did, because it drove one tagging screen at a time.
     * Both arrays reach the API container together.
     */
    const productTagsCanBeChanged =
      apiPublishingAvailable || !hasUserTags || hasUserAndProductTags;
    const userTagsCanBeChanged =
      apiPublishingAvailable || !hasProductTags || hasUserAndProductTags;

    const renderPlatformLabel = (label: string, field: string) => (
      <div className="flex h-6 min-w-0 items-center gap-2">
        <span className="shrink-0 font-inter text-[14px] font-medium leading-5 text-neutral-800">
          {label}
        </span>
        <PlatformUsage
          platforms={platforms}
          field={field}
          selectedPlatform={selectedPlatform}
          variant="other-options"
        />
      </div>
    );

    const renderAction = ({
      label,
      onClick,
      disabled = false,
      testId,
      disabledMessage,
      selectedLabel,
      busy = false,
    }: {
      label: string;
      onClick: () => void;
      disabled?: boolean;
      testId?: string;
      disabledMessage?: string;
      selectedLabel?: string;
      busy?: boolean;
    }) => {
      if (selectedLabel) {
        return (
          <div className="flex min-w-0 items-center gap-1">
            <span
              className="truncate font-inter text-[14px] font-medium leading-5 text-neutral-800"
              title={selectedLabel}
              data-testid="selected-option-value"
            >
              {selectedLabel}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="size-6 shrink-0 rounded-lg p-1 text-neutral-800 shadow-none"
              disabled={disabled}
              onClick={onClick}
              data-testid={testId}
              aria-label={label}
            >
              <i
                className={
                  busy
                    ? "fa-solid fa-spinner fa-spin text-xs"
                    : "fa-regular fa-pen-to-square text-xs"
                }
                aria-hidden="true"
                data-testid={busy ? "tag-action-busy" : undefined}
              />
            </Button>
            {disabled && disabledMessage && (
              <DisabledOptionHelp label={label} message={disabledMessage} />
            )}
          </div>
        );
      }

      const action = (
        <Button
          type="button"
          variant="black-secondary"
          size="xs"
          className="h-6 justify-self-start gap-2 rounded-lg px-2 py-1 font-inter text-xs font-medium leading-4 text-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.05)] [&_svg]:size-3"
          disabled={disabled}
          onClick={onClick}
          data-testid={testId}
          style={{ width: FIGMA_ADD_BUTTON_WIDTHS[label] }}
        >
          <span className="flex size-[15px] shrink-0 items-center justify-center">
            <i
              className={
                busy
                  ? "fa-solid fa-spinner fa-spin text-xs"
                  : "fa-regular fa-plus text-xs"
              }
              aria-hidden="true"
              data-testid={busy ? "tag-action-busy" : undefined}
            />
          </span>
          {label}
        </Button>
      );

      if (disabled && disabledMessage) {
        return (
          <div className="flex items-center gap-1">
            {action}
            <DisabledOptionHelp label={label} message={disabledMessage} />
          </div>
        );
      }

      return action;
    };

    return (
      <section
        className={`${className} flex w-full flex-col gap-2 rounded-lg border-0 bg-white p-4 shadow-[inset_0_0_0_1px_#e6e6e6,0_1px_2px_rgba(16,24,40,0.05)]`}
        data-testid="other-options"
      >
        <h3 className="m-0 font-inter text-[14px] font-semibold leading-5 text-neutral-800">
          Other Options
        </h3>

        <div className="grid w-full grid-cols-2 gap-2">
          {visibleFields.includes(FIELDS.LOCATION_TAG) && (
            <>
              {renderPlatformLabel("Location", FIELDS.LOCATION_TAG)}
              {isSummary ? (
                <p className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800">
                  {location?.name || "No location"}
                </p>
              ) : (
                renderAction({
                  label: `${location ? "Edit" : "Add"} location`,
                  onClick: dispatchLocationModal,
                  selectedLabel: location?.name,
                })
              )}
            </>
          )}

          {visibleFields.includes(FIELDS.COLLABORATOR_TAG) && (
            <>
              {renderPlatformLabel("Collaborators", FIELDS.COLLABORATOR_TAG)}
              {isSummary ? (
                <p className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800">
                  {!collaboratorIg || collaboratorIg.length === 0
                    ? "No collaborators"
                    : collaboratorIg.length === 1
                      ? "1 collaborator"
                      : `${collaboratorIg.length} collaborators`}
                </p>
              ) : (
                renderAction({
                  label: `${collaboratorIg?.length ? "Edit" : "Add"} collaborators`,
                  onClick: dispatchCollaboratorModal,
                  selectedLabel: collaboratorIg?.length
                    ? `${collaboratorIg.length} collaborator${
                        collaboratorIg.length === 1 ? "" : "s"
                      }`
                    : undefined,
                })
              )}
            </>
          )}

          {visibleFields.includes(FIELDS.USER_TAGS) && (
            <>
              {renderPlatformLabel("User tags", FIELDS.USER_TAGS)}
              {isSummary ? (
                <p className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800">
                  {hasUserTags
                    ? `${userTagCount} user tag${userTagCount === 1 ? "" : "s"}`
                    : "No user tags"}
                </p>
              ) : (
                renderAction({
                  label: `${hasUserTags ? "Edit" : "Add"} user tags`,
                  onClick: dispatchUserTagModal,
                  disabled: !userTagsCanBeChanged || stillIsProcessing,
                  busy: stillIsProcessing,
                  disabledMessage: userTagsCanBeChanged
                    ? "This video is still processing. A tag needs a frame to sit on, so this opens once the first frame is ready."
                    : "User tags cannot be added if product tags are added. Remove product tags.",
                  selectedLabel: hasUserTags
                    ? `${userTagCount} user tag${userTagCount === 1 ? "" : "s"}`
                    : undefined,
                })
              )}
              {!isSummary && (
                <TagCapabilityWarning
                  accounts={post.accounts ?? []}
                  applies={firstAssetIsVideo || Boolean(post.isStory)}
                />
              )}
            </>
          )}

          {visibleFields.includes(FIELDS.PRODUCT_TAGS) && (
            <>
              {renderPlatformLabel("Product tags", FIELDS.PRODUCT_TAGS)}
              {isSummary ? (
                <p className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800">
                  {hasProductTags
                    ? `${productTagCount} product tag${
                        productTagCount === 1 ? "" : "s"
                      }`
                    : "No product tags"}
                </p>
              ) : (
                renderAction({
                  label: `${hasProductTags ? "Edit" : "Add"} product tags`,
                  onClick: dispatchProductTagModal,
                  disabled: !productTagsCanBeChanged,
                  disabledMessage:
                    "Product tags cannot be added if user tags are added. Remove user tags.",
                  selectedLabel: hasProductTags
                    ? `${productTagCount} product tag${
                        productTagCount === 1 ? "" : "s"
                      }`
                    : undefined,
                })
              )}
              {!isSummary && (
                <TagCapabilityWarning
                  accounts={post.accounts ?? []}
                  applies={firstAssetIsVideo}
                />
              )}
            </>
          )}

          {hasAlt && visibleFields.includes(FIELDS.ALT_TEXT) && (
            <>
              {renderPlatformLabel("Media alt text", FIELDS.ALT_TEXT)}
              {isSummary ? (
                <p
                  className="m-0 self-center font-inter text-xs font-medium leading-4 text-neutral-800"
                  data-testid="addEditAltTextLabel"
                >
                  {altText.length > 0
                    ? `${altText.length} alt text media`
                    : "No alt text"}
                </p>
              ) : (
                renderAction({
                  label: `${altTextCount > 0 ? "Edit" : "Add"} alt text`,
                  onClick: handleOpenAddAlt,
                  testId: "addEditAltTextButton",
                  selectedLabel:
                    altTextCount > 0
                      ? `${altTextCount} media with alt text`
                      : undefined,
                })
              )}
            </>
          )}
        </div>
        <ChunkLoadRecoveryModal open={requiresReload} onReload={reloadPage} />
      </section>
    );
  } else {
    return null;
  }
};

export default PostTags;
